import { DownloadIcon, Info, ListIcon, LockIcon, SvgIconComponent, TriangleAlert } from 'components/icons';
import { FormattedMessage } from 'react-intl';

import { AiSurface } from 'components/ui/overlay-motion';
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from 'components/ui/popover';
import { FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { cn } from 'utils/cn';

const ABOUT_LINES: Array<[string, SvgIconComponent]> = [
    ['ai-chat.footer.tooltip.review', Info],
    ['ai-chat.footer.tooltip.limit', ListIcon],
    ['ai-chat.footer.tooltip.download', DownloadIcon],
    ['ai-chat.footer.tooltip.access', LockIcon],
];

/**
 * Legal disclaimer shown under the chat composer. Text and full wording are byte-identical across
 * the full AI page, the floating panel, the phone and the meeting brief page (legal requirement) —
 * all render this single component, pointing at the shared `ai-chat.footer.*` i18n keys.
 *
 * About AI answers opens on click (and dismisses on an outside press) rather than on hover, so it can
 * be read at leisure and reached on touch devices. The disclaimer is its own AI surface: the meeting
 * brief page is not one, and there it still draws the new design's tokens, its popover included, which
 * portals above the floating panel.
 */
const AiChatDisclaimer: React.FC<{
    /** The composer above the disclaimer: About AI answers opens above it, centred on it and no wider than it. */
    composer?: HTMLElement | null;
}> = ({ composer }) => (
    <AiSurface className="text-caption text-muted-foreground w-full pt-2 text-center text-balance">
        <TriangleAlert className="me-1 inline-block size-3.5 align-[-3px]" />
        <FormattedMessage id="ai-chat.footer.disclaimer" />{' '}
        <Popover>
            <PopoverTrigger
                className={cn('text-foreground hover:text-link hit-area relative font-medium underline', FOCUS_RING)}
            >
                <FormattedMessage id="ai-chat.footer.about" />
            </PopoverTrigger>
            <PopoverContent
                anchor={composer}
                side="top"
                sideOffset={composer ? 36 : 8}
                className={cn(
                    'text-meta text-foreground-secondary gap-2.5 rounded-[12px] text-start',
                    composer ? 'w-[min(420px,calc(var(--anchor-width)-8px))]' : 'w-[min(420px,calc(100vw-2rem))]'
                )}
            >
                <PopoverTitle className="text-section text-foreground">
                    <FormattedMessage id="ai-chat.footer.about" />
                </PopoverTitle>
                <ul className="flex flex-col gap-2.5">
                    {ABOUT_LINES.map(([id, Icon]) => (
                        <li key={id} className="flex gap-2.5">
                            <Icon className="text-foreground-tertiary mt-0.5 size-4 shrink-0" />
                            <FormattedMessage id={id} />
                        </li>
                    ))}
                </ul>
            </PopoverContent>
        </Popover>
    </AiSurface>
);

export default AiChatDisclaimer;
